"use client";

import type { CSSProperties } from "react";
import type { TrustedByContent } from "@/components/landing/registry/content";
import type { TickerLogo } from "@/components/landing/TrustStrip/types";
import { MotionReveal, useSectionMotion } from "@/components/landing/shared";

function LogoPill({ logo }: { logo: TickerLogo }) {
	if (logo.imageUrl) {
		return (
			// oxlint-disable-next-line nextjs/no-img-element -- landing logo, arbitrary dimensions
			<img
				src={logo.imageUrl}
				alt={logo.label}
				className="h-(--jana-logo-size) w-auto object-contain"
			/>
		);
	}
	return (
		<div className="whitespace-nowrap rounded-full border border-border bg-bg/70 px-4 py-2 text-sm font-semibold">
			{logo.label}
		</div>
	);
}

function TrustStripInner({
	eyebrow,
	showHeading,
	content,
}: {
	eyebrow: string;
	showHeading: boolean;
	content: TrustedByContent;
}) {
	const motion = useSectionMotion();
	const enabled = content.logos.filter((l) => l.enabled);
	const vars: CSSProperties = {
		["--jana-marquee-duration" as string]: `${Math.max(6, 90 - content.speed)}s`,
		["--jana-marquee-direction" as string]: content.direction === "rtl" ? "reverse" : "normal",
		["--jana-logo-size" as string]: `${content.logoSize}px`,
		gap: `${content.gap}px`,
	};
	const maskStyle: CSSProperties = content.fadeEdges
		? {
				maskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
				WebkitMaskImage: "linear-gradient(90deg, transparent, black 8%, black 92%, transparent)",
			}
		: {};

	return (
		<div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 md:flex-row md:items-center">
			{showHeading && eyebrow && (
				<MotionReveal motion={motion} className="shrink-0">
					<p className="text-sm font-medium text-muted-foreground">{eyebrow}</p>
				</MotionReveal>
			)}

			{content.infiniteLoop ? (
				<MotionReveal
					motion={motion}
					delayOffset={50}
					className={[
						"min-w-0 flex-1 overflow-hidden",
						content.pauseOnHover ? "jana-marquee-pause" : "",
					].join(" ")}
					style={maskStyle}
				>
					<div className="flex w-max items-center jana-marquee-track" style={vars}>
						{[...enabled, ...enabled].map((logo, i) => (
							<div key={`${logo.id}-${i}`} className="flex shrink-0 items-center">
								<LogoPill logo={logo} />
							</div>
						))}
					</div>
				</MotionReveal>
			) : (
				<MotionReveal
					motion={motion}
					delayOffset={50}
					className="flex flex-1 flex-wrap gap-3 md:justify-end"
				>
					{enabled.map((logo) => (
						<LogoPill key={logo.id} logo={logo} />
					))}
				</MotionReveal>
			)}
		</div>
	);
}

export default TrustStripInner;
